import { createCachedSelector } from 're-reselect';
import { useCallback } from 'react';
import { useParams, generatePath, useRouteMatch, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import moment from 'moment';
import * as api from '../../services/api';
import useUser from '../auth/useUser';
import { ACCOUNT_BILLING_STATUS_MAP, API_DATE_FORMAT, QUERY_CACHE_TIME, TIME_FORMAT } from '../../constants';
import { RTL_MAP } from '../useTranslatedApp';
import useToaster from '../useToaster';

const selector = createCachedSelector(
  (data) => data.data,
  (data, adminLanguage) => adminLanguage,
  (data, adminLanguage) => {
    const language = adminLanguage || data?.language;
    const account = { ...(data || {}), language: language?.iso6391, languageId: language?.id };

    const distanceUnit = account.distanceUnit || 'km';

    const config = {
      accountId: account?.id,
      hourFormat: parseInt(account?.hourFormat, 10) || 24,
      timeFormat: TIME_FORMAT[parseInt(account?.hourFormat, 10)] || TIME_FORMAT[24],
      isHost: account?.isHost,
      organizationId: account?.organizationId,
      dateFormat: account?.dateFormat || 'DD/MM/YYYY',
      shortDateFormat: account?.dateFormat?.replace('YYYY', 'YY') || 'DD/MM/YY',
      shortDateMonthFormat: account?.dateFormat?.replace('/YYYY', '') || 'DD/MM',
      firstDayOfWeek: account?.firstDayOfWeek || 0,
      currency: account?.currency?.symbol || '$',
      countryCode: account?.country?.code || 'us',
      industryId: account?.industryId,
      showOnboarding: account?.showOnboarding,
      isRtl: RTL_MAP[account?.language] || false,
      languageName: account?.language,
      payrollPeriodTypeId: account?.payrollPeriodTypeId,
      paymentStartDate: moment(account?.payrollConfig?.paymentStartDate).format(API_DATE_FORMAT),
      isPayRatesEnabled: account?.payrollConfig?.isPayRatesEnabled,
      distanceUnit,
      distanceUnitShortTranslationKey: `distanceUnit_${distanceUnit}_short`,
      disableEmployeeIdField: !!account.customSettings?.disableEmployeeIdField,
      disableClientAddFromEvent: !!account.customSettings?.disableClientAddFromEvent,
      enableClientCostCenterFeature: !!account.customSettings?.enableClientCostCenterFeature,
      enableContractFeature: !!account.customSettings?.enableContractsFeature,
      enableContractLeaveManagementFeature: !!account.customSettings?.enableContractLeaveManagementFeature,
      enableLiveView: !!account?.enableLiveView,
      enablePayrollProcessFeature: !!account.customSettings?.enablePayrollProcessFeature,
      enablePayrollProcessJobsFeature: !!account.customSettings?.enablePayrollProcessJobsFeature,
      enablePayrollPeriodRequeryFeature: !!account.customSettings?.enablePayrollPeriodRequeryFeature,
      payrollCheckInOut: !!account.customSettings?.payrollCheckInOut,

      isProjectStatsActualBooked: !!account.customSettings?.projectStatsActualBookedFeature,
      isEngagementAccount: !!account.customSettings?.isEngagementAccount,
      enableRulesEngineFeature: !!account.customSettings?.enableRulesEngineFeature,
      enableOrganizationalPositionsFeature: !!account.customSettings?.enableOrganizationalPositionsFeature,
      enableClientTimesheetApprovalNotification: !!account.customSettings?.enableClientTimesheetApprovalNotification,
      enableExportSystemEmployeesFeature: !!account.customSettings?.enableExportSystemEmployeesFeature,
      enableClientSelectEmployeesFeature: !!account.customSettings?.enableClientSelectEmployeesFeature,
      exportSystemEmployeesMode: account.customSettings?.exportSystemEmployeesMode || 'manual',
      isAccountNetworkAccessSupplier: [
        ACCOUNT_BILLING_STATUS_MAP.NETWORK_ACCESS_UNLIMITED_ADMINS,
        ACCOUNT_BILLING_STATUS_MAP.STAFFING_AGENCY_FREEMIUM
      ].includes(account?.settings?.billing?.status?.type),
      billingStatus: account?.settings?.billing?.status?.type,
      isFreemiumCopyEventEnabled:
        account?.settings?.billing?.status?.type === ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM &&
        !!account.customSettings?.enableFreemiumCopyEvent,
      billingCurrency: account?.settings?.billing?.currency?.symbol,
      showHACKInvoiceNumber: [48988, 164, 50333].includes(account?.id),
      hasTimeAvailability: !!(account?.weeklyPreference || account?.recurringAvailability),
      unavailableByDefault:
        !!account?.unavailableByDefault && !!(account?.weeklyPreference || account?.recurringAvailability),
      isAiAutoScheduleEnabled: !!account.customSettings?.isAiAutoScheduleEnabled,
      isEventPlannerAgentEnabled: !!account.customSettings?.isEventPlannerAgentEnabled,
      isSchedulingAgentEnabled: !!account.customSettings?.isSchedulingAgentEnabled,
      isQueryTotalHoursOverFlinkEnabled: !!account.customSettings?.queryTotalHoursOverFlink,
      isCrewTimesheetPageEnabled: !!account.customSettings?.isCrewTimesheetPageEnabled,
      isAiAgentA2uiEnabled: !!account.customSettings?.isAiAgentA2uiEnabled,
      isOrganizationDashboardEnabled: !!account.customSettings?.isOrganizationDashboardEnabled,
      isEmployeePreferredAreasEnabled: !!account.customSettings?.isEmployeePreferredAreasEnabled,
      enableSupplierFreemiumExports: !!account.customSettings?.enableSupplierFreemiumExports,
      isAvailabilityWindowEnabled: !!account.customSettings?.isAvailabilityWindowEnabled,
      isSchedulingBoardBudgetEnabled: !!account.customSettings?.isSchedulingBoardBudgetEnabled,
      isBlockDemographicFiltersInSchedulingEnabled: !!account.customSettings
        ?.isBlockDemographicFiltersInSchedulingEnabled,
      isPayWageScaleUpliftEnabled: !!account.customSettings?.isPayWageScaleUpliftEnabled
    };

    return { account, config };
  }
)({
  keySelector: (_data, _adminLanguage, storeKey) => storeKey
});

const useAccount = ({ enabled = true } = {}) => {
  const { accounts = [], refetch, language } = useUser();
  const { accountId: paramAccountId } = useParams();
  const { path } = useRouteMatch();
  const { push } = useHistory();
  const { addResponseError } = useToaster();

  const accountId =
    parseInt(paramAccountId, 10) || accounts?.find(({ isDefault }) => !!isDefault)?.id || accounts?.[0]?.id;
  const storeKey = ['account', accountId];
  const { isLoading, isSuccess, data } = useQuery(storeKey, () => api.fetchAccount({ accountId }), {
    enabled: !!accountId && enabled,
    cacheTime: QUERY_CACHE_TIME,
    select: (accountData) => selector(accountData, language, storeKey.join('#'))
  });
  const { account = {}, config = {} } = data || {};

  const setSelectedAccount = useCallback(
    (newAccountId) => {
      const newPath = generatePath(path, { accountId: newAccountId });
      push(newPath);
    },
    [path, push]
  );

  const { mutateAsync: addAccount } = useMutation(api.addAccount, { throwOnError: true, onError: addResponseError });

  return {
    isLoading,
    isSuccess,
    accountId,
    account,
    setSelectedAccount,
    config,
    addAccount,
    refetch
  };
};

export default useAccount;
